import { FC, useMemo } from "react";
import {
  AddressId,
  CustomerId,
  Nullable,
  serviceTypeEnumLabelMap,
  useAdminLocationControllerUpdateLocationServiceAccess,
  useAdminLocationControllerDelete,
  useAdminLocationControllerUpdate,
} from "service";
import GenericTable, {
  RenderGenericRowFunction,
  SortOptions,
} from "../components/generic-table/generic-table";
import CheckLabel from "../customer-management/check-label";
import {
  Button,
  Checkbox,
  Chip,
  useTheme,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { AdRoutes } from "../../../constants/ad-routes";
import { generateRoute, ServiceTimeIcon, ElevationIcon } from "ui";
import InteractableCell from "../components/generic-table/interactable-cell";
import { useDialogStore } from "../../../state/dialog-store";
import { toast } from "react-toastify";
import {
  addressIso,
  formattedValueWithSymbol,
  formatDateTime,
} from "../../../utils/string-helpers";
import { AdminCustomerLocationResDto } from "service/api/estabrookSchemas";
import { queryClient } from "../../../query-client";
import { queryKeyFn } from "service/api/estabrookContext";
import EditableCell from "../components/generic-table/editable-cell";

export interface AddressManagementTableProps {
  data?: AdminCustomerLocationResDto[];
  sortOptions?: Nullable<SortOptions>;
  sortOptionsChange?: (value: Nullable<SortOptions>) => void;
  isLoading?: boolean;
  selectedLocationIds: string[];
  handleLocationSelection: (
    evt: React.ChangeEvent<HTMLInputElement>,
    locationId: string,
  ) => void;
  handleLocationSelectionAll: (
    evt: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onDownloadReport?: (locationId: string, subtitle: string) => void;
}

const AddressManagementTableCombo: FC<AddressManagementTableProps> = (
  props,
) => {
  const {
    data = [],
    sortOptions,
    sortOptionsChange,
    isLoading,
    handleLocationSelection,
    handleLocationSelectionAll,
    selectedLocationIds,
    onDownloadReport,
  } = props;
  const navigate = useNavigate();
  const { setDialog } = useDialogStore();
  const { palette } = useTheme();

  const { mutate } = useAdminLocationControllerUpdate();

  const { mutate: deleteLocation } = useAdminLocationControllerDelete({
    onSuccess: () => {
      toast.success("Address Deleted Successfully");
      invalidateQueries();
    },
  });

  const { mutate: changeServiceAccess, isLoading: isServiceAccessLoading } =
    useAdminLocationControllerUpdateLocationServiceAccess({
      onSuccess: (_, variables) => {
        toast.success("Service Access has been Updated Successfully");
        invalidateQueries(variables.pathParams.locationId);
      },
    });

  const onAddressDetail = (addressId: AddressId, customerId: CustomerId) => {
    navigate(
      generateRoute(AdRoutes.customerDetailAddress, {
        addressId,
        customerId,
      }),
    );
  };

  const onDeletePress = async (addressId: AddressId, title: string) => {
    setDialog({
      title: "Delete address",
      description: `Are you sure you want to delete the address "${title}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      sx: {
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "400px",
          },
        },
      },
      onConfirm: async (onClose) => {
        deleteLocation({ pathParams: { locationId: addressId } });
        onClose();
      },
    });
  };

  const invalidateQueries = (locationId?: string) => {
    queryClient.invalidateQueries(
      queryKeyFn({
        path: "/api/v1/admin/customers-locations",
        operationId: "adminCustomerLocationControllerGetAll",
        variables: {},
      }),
    );
    // Invalidate individual location cache if locationId is provided
    if (locationId) {
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/locations/{locationId}",
          operationId: "adminLocationControllerGetOne",
          variables: {
            pathParams: { locationId },
          },
        }),
      );
    }
  };

  const uniqueLocationIds = Array.from(
    new Set(data?.map((item) => item.locationId).filter(Boolean) || []),
  );

  const isAllSelected =
    uniqueLocationIds.length > 0 &&
    selectedLocationIds.length === uniqueLocationIds.length;

  const sanitizedHeader = useMemo(() => {
    return [
      {
        label: (
          <Checkbox
            checked={isAllSelected}
            onChange={(evt) => handleLocationSelectionAll(evt)}
          />
        ),
        value: "checkbox",
        size: "4%",
        sortable: false,
      },
      {
        label: (
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: palette.grey[100] }}
          >
            Address
          </Typography>
        ),
        value: "address",
        size: "20%",
        sortable: true,
      },
      {
        label: (
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: palette.grey[100] }}
          >
            Customer
          </Typography>
        ),
        value: "email",
        size: "16%",
        sortable: true,
        sortableValue: "fullName",
      },
      {
        label: (
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: palette.grey[100] }}
          >
            Region
          </Typography>
        ),
        value: "region",
        size: "10%",
        sortable: true,
      },
      {
        label: (
          <Tooltip title="Service Time">
            <Box sx={{ display: "inline-flex", cursor: "pointer" }}>
              <ServiceTimeIcon />
            </Box>
          </Tooltip>
        ),
        value: "serviceTime",
        size: "6%",
        sortable: true,
        minWidth: "60px",
      },
      {
        label: (
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: palette.grey[100] }}
          >
            Service Type
          </Typography>
        ),
        value: "type",
        size: "8%",
      },
      {
        label: (
          <Tooltip title="Elevation">
            <Box sx={{ display: "inline-flex", cursor: "pointer" }}>
              <ElevationIcon />
            </Box>
          </Tooltip>
        ),
        value: "elevation",
        size: "8%",
        sortable: true,
      },
      {
        label: (
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: palette.grey[100] }}
          >
            Last Plowed
          </Typography>
        ),
        value: "lastPlowed",
        size: "8%",
      },
      {
        label: (
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: palette.grey[100] }}
          >
            Verified
          </Typography>
        ),
        value: "emailVerificationStatus",
        sortable: true,
        sortableValue: "emailConfirmed",
        size: "6%",
      },
      {
        label: (
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: palette.grey[100],
              }}
            >
              Service Access
            </Typography>
            <Tooltip title="Controls whether this customer can access the app and be included in routing and service operations.">
              <InfoOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
            </Tooltip>
          </Box>
        ),
        value: "serviceAccess",
        sortable: true,
        sortableValue: "serviceAccess",
        size: "8%",
        minWidth: "120px",
      },
      {
        label: (
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: palette.grey[100] }}
          >
            Snow Reporter
          </Typography>
        ),
        value: "reportsEnabled",
        sortable: true,
        size: "6%",
      },
      {
        label: "",
        value: "actions",
        size: "8%",
        minWidth: "100px",
      },
    ];
  }, [selectedLocationIds, data]);

  const renderRowLabelMap: Record<
    string,
    RenderGenericRowFunction<AdminCustomerLocationResDto>
  > = useMemo(
    () => ({
      checkbox: ({ headerValue, data: { locationId } }) => {
        return {
          label: (
            <InteractableCell
              label={
                <Checkbox
                  checked={selectedLocationIds.includes(locationId)}
                  onChange={(evt) => handleLocationSelection(evt, locationId)}
                />
              }
            />
          ),
          value: headerValue,
        };
      },
      address: ({ headerValue, data }) => ({
        label: (
          <Typography variant="body2" noWrap>
            {addressIso(data)}
          </Typography>
        ),
        value: headerValue,
      }),
      email: ({ headerValue, data: { firstName, lastName, email } }) => ({
        label: (
          <Box>
            <Typography>{`${firstName} ${lastName}`}</Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#C1C7CE",
              }}
            >
              {email}
            </Typography>
          </Box>
        ),
        value: headerValue,
      }),
      region: ({ headerValue, data: { region } }) => ({
        label: region?.name ? <Chip label={region?.name} /> : "No Region",
        value: headerValue,
      }),
      serviceTime: ({ headerValue, data: { serviceTime, locationId } }) => ({
        label: (
          <EditableCell
            id={locationId}
            idHandle={"locationId"}
            label={serviceTime}
            prop="serviceTime"
            append="min"
            mutate={mutate}
            refetch={invalidateQueries}
          />
        ),
        value: headerValue,
      }),
      elevation: ({ headerValue, data: { elevation, locationId } }) => ({
        label: locationId
          ? formattedValueWithSymbol(elevation.imperial)
          : "N/A",
        value: headerValue,
      }),
      lastPlowed: ({ headerValue, data: { lastPlow } }) => ({
        label: lastPlow ? formatDateTime(lastPlow) : "N/A",
        value: headerValue,
      }),
      emailVerificationStatus: ({ headerValue, data: { emailConfirmed } }) => ({
        label: <CheckLabel isChecked={emailConfirmed} />,
        value: headerValue,
      }),
      serviceAccess: ({
        headerValue,
        data: { serviceAccess, locationId, appAccess, address },
      }) => ({
        label: (
          <Tooltip
            title={
              appAccess === false
                ? "Customer service access is disabled. Enable customer service access first to edit address service access."
                : "Click to toggle service access."
            }
          >
            <span>
              <Button
                sx={{ maxWidth: "50px" }}
                onClick={() => {
                  if (appAccess === false) {
                    toast.error(
                      `This customer has service access disabled. You are not allowed to edit the service access for address "${address}". Please enable customer service access first.`,
                    );
                    return;
                  }
                  changeServiceAccess({
                    pathParams: { locationId: locationId },
                    body: {
                      serviceAccess: !serviceAccess,
                    },
                  });
                }}
                disabled={isServiceAccessLoading || appAccess === false}
              >
                <CheckLabel isChecked={serviceAccess} />
              </Button>
            </span>
          </Tooltip>
        ),
        value: headerValue,
      }),
      reportsEnabled: ({ headerValue, data }) => {
        const { reportsEnabled } = data;
        return {
          label: <CheckLabel isChecked={reportsEnabled} />,
          value: headerValue,
        };
      },
      actions: ({ headerValue, data }) => {
        const { locationId, customerId, firstName, lastName } = data;
        return {
          label: (
            <InteractableCell
              label={""}
              {...(onDownloadReport &&
                locationId && {
                  onDownload: () =>
                    onDownloadReport(
                      locationId,
                      `${firstName} ${lastName} - ${addressIso(data)}`,
                    ),
                })}
              onEdit={() => onAddressDetail(locationId, customerId)}
              {...(locationId && {
                onDelete: () => onDeletePress(locationId, addressIso(data)),
              })}
            />
          ),
          value: headerValue,
        };
      },
      type: ({ headerValue, data }) => {
        const { type } = data;
        return {
          label: type ? (
            <Chip
              label={serviceTypeEnumLabelMap[type]}
              sx={{
                backgroundColor:
                  type === "annual" ? palette.grey[400] : palette.grey[500],
              }}
            />
          ) : (
            "N/A"
          ),
          value: headerValue,
        };
      },
    }),
    [selectedLocationIds, onDownloadReport],
  );

  return (
    <GenericTable
      sx={{ mt: "20px" }}
      headers={sanitizedHeader}
      renderRowLabel={renderRowLabelMap}
      data={data}
      sortOptions={sortOptions}
      onSortChange={sortOptionsChange}
      isLoading={isLoading}
    />
  );
};

export default AddressManagementTableCombo;
